import { useState } from "react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { TOKEN_KEY } from "../lib/api";
import { DocViewerModal } from "./DocViewer";
import { Camera, UploadSimple, CircleNotch, Signature, CheckCircle, Truck, IdentificationCard } from "@phosphor-icons/react";

function fileUrl(id) {
  return `${API}/files/${id}/download?auth=${localStorage.getItem(TOKEN_KEY)}`;
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const up = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return up.data.id;
}

export function ServiceEvidence({ serviceId, svc, isCarrier, onSaved }) {
  const [busy, setBusy] = useState(null);
  const evidence = svc.evidence || { pickup: [], delivery: [] };
  const pod = svc.pod;
  const done = ["COMPLETADO", "CANCELADO"].includes(svc.status);

  const addPhotos = async (stage, files) => {
    if (!files?.length) return;
    setBusy(stage);
    try {
      const ids = [];
      for (const f of files) ids.push(await uploadFile(f));
      await api.post(`/services/${serviceId}/evidence`, { stage, fileIds: ids });
      toast.success("Evidencia adjuntada");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    setBusy(null);
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="service-evidence">
      <h2 className="font-semibold mb-1 flex items-center gap-2"><Camera size={20} className="text-[#FF5A36]" /> Evidencias y comprobante de entrega</h2>
      <p className="text-sm text-zinc-500 mb-3">Fotos de retiro y entrega + firma de quien recibe (POD).</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <EvidenceGroup title="Retiro" stage="PICKUP" ids={evidence.pickup} isCarrier={isCarrier}
          busy={busy === "PICKUP"} done={done} onAdd={addPhotos} testid="evidence-pickup" />
        <EvidenceGroup title="Entrega" stage="DELIVERY" ids={evidence.delivery} isCarrier={isCarrier}
          busy={busy === "DELIVERY"} done={done} onAdd={addPhotos} testid="evidence-delivery" />
      </div>

      <PodBlock serviceId={serviceId} pod={pod} isCarrier={isCarrier} done={done} onSaved={onSaved} />
    </div>
  );
}

function EvidenceGroup({ title, stage, ids = [], isCarrier, busy, done, onAdd, testid }) {
  const [preview, setPreview] = useState(null);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{title}</span>
        {isCarrier && !done && (
          <label className="text-sm font-semibold text-[#FF5A36] cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid={`${testid}-add`}>
            {busy ? <CircleNotch size={16} className="animate-spin" /> : <UploadSimple size={16} />} Agregar fotos
            <input type="file" accept="image/*" multiple capture="environment" className="hidden" disabled={busy}
              onChange={(e) => onAdd(stage, Array.from(e.target.files))} />
          </label>
        )}
      </div>
      {ids.length === 0 ? (
        <p className="text-sm text-zinc-400">Sin fotos aún.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {ids.map((id) => (
            <button key={id} type="button" onClick={() => setPreview(id)} data-testid={`${testid}-photo-${id}`}
              className="block rounded-lg overflow-hidden border border-zinc-200 hover:opacity-90 transition-opacity active:scale-95">
              <img src={fileUrl(id)} alt={title} className="w-full h-24 object-cover" />
            </button>
          ))}
        </div>
      )}
      <DocViewerModal fileId={preview} title={`Evidencia · ${title}`} onClose={() => setPreview(null)} />
    </div>
  );
}

function PodBlock({ serviceId, pod, isCarrier, done, onSaved }) {
  const [form, setForm] = useState({ receiverName: "", receiverRut: "" });
  const [sigId, setSigId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const uploadSig = async (file) => {
    if (!file) return;
    setBusy(true);
    try { setSigId(await uploadFile(file)); toast.success("Firma adjuntada"); }
    catch { toast.error("Error al subir la firma"); }
    setBusy(false);
  };

  const save = async () => {
    if (!form.receiverName.trim()) return toast.error("Indica el nombre de quien recibe");
    setBusy(true);
    try {
      await api.post(`/services/${serviceId}/pod`, { ...form, signatureFileId: sigId });
      toast.success("Comprobante de entrega guardado");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  if (pod) {
    return (
      <div className="border-t border-zinc-100 pt-4 mt-2" data-testid="pod-readonly">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-2"><CheckCircle size={18} weight="fill" /> Comprobante de entrega firmado</div>
        <div className="text-sm text-zinc-700">Recibió: <span className="font-semibold">{pod.receiverName}</span>{pod.receiverRut ? ` · ${pod.receiverRut}` : ""}</div>
        {pod.signatureFileId && (
          <>
            <button type="button" onClick={() => setPreview(pod.signatureFileId)} data-testid="pod-signature" className="block active:scale-95">
              <img src={fileUrl(pod.signatureFileId)} alt="Firma" className="mt-2 h-24 rounded-lg border border-zinc-200 bg-zinc-50 object-contain hover:opacity-90 transition-opacity" />
            </button>
            <DocViewerModal fileId={preview} title="Firma · Comprobante de entrega" onClose={() => setPreview(null)} />
          </>
        )}
      </div>
    );
  }

  if (!isCarrier) return <div className="border-t border-zinc-100 pt-4 mt-2 text-sm text-zinc-400" data-testid="pod-pending">El transportista aún no registra el comprobante de entrega.</div>;
  if (done) return null;

  return (
    <div className="border-t border-zinc-100 pt-3 mt-3 space-y-3" data-testid="pod-form">
      <div className="flex items-center gap-2 text-sm font-semibold"><Signature size={18} className="text-[#FF5A36]" /> Comprobante de entrega (POD)</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="pod-receiver-name" value={form.receiverName} onChange={(e) => setForm({ ...form, receiverName: e.target.value })}
          placeholder="Nombre de quien recibe" className="w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
        <input data-testid="pod-receiver-rut" value={form.receiverRut} onChange={(e) => setForm({ ...form, receiverRut: e.target.value })}
          placeholder="RUT de quien recibe (opcional)" className="w-full h-11 px-4 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36]" />
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-semibold text-[#FF5A36] cursor-pointer inline-flex items-center gap-1 hover:underline" data-testid="pod-signature-upload">
          {busy ? <CircleNotch size={16} className="animate-spin" /> : <Signature size={16} />} {sigId ? "Firma adjuntada ✓" : "Adjuntar firma / foto"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadSig(e.target.files[0])} />
        </label>
        <button onClick={save} disabled={busy} data-testid="pod-save"
          className="px-6 py-2.5 rounded-xl bg-[#0F1115] text-white font-bold hover:bg-[#1A1D24] disabled:opacity-60 active:scale-95">Guardar comprobante</button>
      </div>
    </div>
  );
}

export function AssignedResources({ svc }) {
  const v = svc.vehicle;
  const d = svc.driver;
  if (!v && !d) return null;
  return (
    <div className="border-t border-zinc-100 mt-3 pt-3 grid sm:grid-cols-2 gap-4" data-testid="assigned-resources">
      <div className="flex items-start gap-2">
        <Truck size={18} className="text-zinc-400 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Vehículo asignado</div>
          <div className="font-medium text-sm">{v ? `${v.type}${v.licensePlate ? ` · ${v.licensePlate}` : ""}` : "—"}</div>
          {v?.capacity && <div className="text-xs text-zinc-500">Capacidad: {v.capacity}</div>}
        </div>
      </div>
      <div className="flex items-start gap-2">
        <IdentificationCard size={18} className="text-zinc-400 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold flex items-center gap-1">Conductor {d?.verified && <CheckCircle size={13} weight="fill" className="text-emerald-500" />}</div>
          <div className="font-medium text-sm">{d?.name || "—"}</div>
          <div className="text-xs text-zinc-500">{d?.licenseClass ? `Licencia ${d.licenseClass}` : ""}{d?.cedula ? ` · ${d.cedula}` : ""}</div>
        </div>
      </div>
    </div>
  );
}
